import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { usePlan } from '../contexts/PlanContext';
import { Key, Plus, Trash2, Copy, Loader2, Lock, Shield, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchWithAuth = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}/api/v1${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail || 'Request failed');
  }
  return response.json();
};

const SettingsPage = () => {
  const { hasFeature, planName } = usePlan();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const apiAccessible = hasFeature('api_access') && planName !== 'Free';

  const loadKeys = useCallback(async () => {
    if (!apiAccessible) {
      setLoading(false);
      return;
    }
    try {
      const data = await fetchWithAuth('/api-keys');
      setKeys(data);
    } catch (err) {
      if (!err.message.includes('not available')) toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiAccessible]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      const result = await fetchWithAuth('/api-keys', {
        method: 'POST',
        body: JSON.stringify({ name: newKeyName.trim() })
      });
      setCreatedKey(result);
      setNewKeyName('');
      loadKeys();
      toast.success('API key created');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await fetchWithAuth(`/api-keys/${keyId}`, { method: 'DELETE' });
      toast.success('API key revoked');
      loadKeys();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage API keys and integrations</p>
      </div>

      {/* API Keys Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Key className="w-4 h-4" /> API Keys
              </CardTitle>
              <CardDescription>Use API keys to integrate live inventory data with your website or external systems</CardDescription>
            </div>
            {apiAccessible && (
              <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-api-key-btn">
                <Plus className="w-4 h-4 mr-1" /> Create Key
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!apiAccessible ? (
            <div className="text-center py-8 space-y-3">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto">
                <Lock className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="font-medium text-sm">API access requires a paid plan</p>
              <p className="text-xs text-muted-foreground">Upgrade to Starter or above to generate API keys and integrate live inventory.</p>
              <Button size="sm" variant="outline" onClick={() => window.location.href = '/pricing'} data-testid="upgrade-for-api">
                View Plans
              </Button>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-3">
              {keys.length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-6">No API keys yet. Create one to get started.</p>
              )}
              {keys.map(k => (
                <div key={k.key_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`api-key-${k.key_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium text-sm">{k.name}</span>
                      <Badge variant="outline" className="text-[10px]">{k.permissions?.join(', ')}</Badge>
                    </div>
                    <code className="text-xs text-muted-foreground mt-1 block font-mono">{k.key_masked || k.key}</code>
                    {k.last_used && (
                      <p className="text-[10px] text-muted-foreground mt-1">Last used: {new Date(k.last_used).toLocaleString()}</p>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleRevoke(k.key_id)} data-testid={`revoke-key-${k.key_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {/* API Documentation */}
              <div className="mt-6 p-4 bg-muted/30 rounded-lg space-y-3">
                <p className="font-medium text-sm">Quick Start</p>
                <p className="text-xs text-muted-foreground">Use your API key in the <code className="bg-muted px-1 rounded">X-API-Key</code> header:</p>
                <pre className="text-xs bg-zinc-950 text-green-400 p-3 rounded overflow-x-auto">
{`# Get inventory summary
curl -H "X-API-Key: YOUR_KEY" \\
  ${API_URL}/api/v1/public/projects/{'{project_id}'}/inventory-summary

# Get available units
curl -H "X-API-Key: YOUR_KEY" \\
  ${API_URL}/api/v1/public/projects/{'{project_id}'}/available-units`}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) setCreatedKey(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdKey ? 'API Key Created' : 'Create API Key'}</DialogTitle>
          </DialogHeader>
          {createdKey ? (
            <div className="space-y-4 py-4">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">Copy this key now. You won't be able to see it again.</p>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono bg-muted p-3 rounded break-all" data-testid="created-api-key">{createdKey.key}</code>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(createdKey.key)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Key Name</Label>
                <Input
                  value={newKeyName}
                  onChange={e => setNewKeyName(e.target.value)}
                  placeholder="e.g. Website Integration"
                  data-testid="api-key-name-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {createdKey ? (
              <Button onClick={() => { setShowCreate(false); setCreatedKey(null); }}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={!newKeyName.trim()} data-testid="confirm-create-key">Create</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
